import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type {
	CreateTerritoryInput,
	ImportSelection,
	ImportTerritoryParams,
	ListTerritoriesParams,
	UpdateTerritoryInput,
} from "../services/territory.service";
import { territoryService } from "../services/territory.service";

export const TERRITORY_QUERY_KEY = "territories";

export function useCreateTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateTerritoryInput) => territoryService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			message.success("Hudud muvaffaqiyatli yaratildi");
		},
	});
}

export function useUpdateTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTerritoryInput }) =>
			territoryService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY, "detail", variables.id] });
			message.success("Hudud muvaffaqiyatli yangilandi");
		},
	});
}

export function useDeleteTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => territoryService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			message.success("Hudud muvaffaqiyatli o'chirildi");
		},
	});
}

export function useTerritories(params: ListTerritoriesParams, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [TERRITORY_QUERY_KEY, "list", params],
		queryFn: () => territoryService.list(params),
		enabled: !!params?.organizationId && (options?.enabled ?? true),
	});
}

export function useTerritory(id?: string) {
	return useQuery({
		queryKey: [TERRITORY_QUERY_KEY, "detail", id],
		queryFn: () => territoryService.getById(id as string),
		enabled: !!id,
	});
}

export function useImportTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ImportTerritoryParams) => territoryService.import(params),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: [TERRITORY_QUERY_KEY, "list", { organizationId: variables.organizationId }],
			});
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			message.success("Hudud import qilindi");
		},
	});
}

export function useAssignStaffToTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			territoryId,
			staffIds,
			isPrimary,
		}: {
			territoryId: string;
			staffIds: string[];
			isPrimary?: boolean;
		}) => {
			const body = staffIds.map((staffId, index) => ({
				staffId,
				isPrimary: index === 0 ? (isPrimary ?? true) : false,
			}));
			return territoryService.assignStaff(territoryId, body);
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			const count = variables.staffIds.length;
			message.success(count > 1 ? `${count} ta xodim biriktirildi` : "Xodim biriktirildi");
		},
	});
}

export function usePreviewImportTerritory() {
	return useMutation({
		mutationFn: (params: { file: File; organizationId: string }) =>
			territoryService.previewImport(params),
	});
}

export function useConfirmImportTerritory() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { file: File; organizationId: string; selections: ImportSelection[] }) =>
			territoryService.confirmImport(params),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: [TERRITORY_QUERY_KEY] });
			const count = data.territories.length;
			message.success(count > 1 ? `${count} ta hudud import qilindi` : "Hudud import qilindi");
		},
	});
}
